import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import JoditEditor from "jodit-react";
import { Icon } from "@iconify/react";
import { useQuery } from "../../../Hooks/useApi";

const SuperAdminFooter = () => {
  const { data: footerResponse } = useQuery("/footer/getFooter");
  const [formData, setFormData] = useState({
    termsAndCondition: "",
    privacyPolicy: "",
    location: "",
    description: "",
    phone: "",
    email: "",
    facebook: "",
    instagram: "",
    linkedin: "",
  });
  const termsRef = useRef(null);
  const policyRef = useRef(null);

  useEffect(() => {
    if (footerResponse?.data?.length) {
      setFormData(footerResponse.data[0]);
    }
  }, [footerResponse]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (formData?._id) {
        await UpdateHooks(
          `https://servercareseebd.careseebd.com/api/v1/footer/updateFooter/${formData?._id}`,
          formData
        );
        toast.success("Footer details updated successfully!");
      } else {
        await PostHooks(
          `https://servercareseebd.careseebd.com/api/v1/footer/addFooter`,
          formData,
          "Footer data created successfully!"
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="max-w-5xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:globus-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Footer & Legal Settings</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Manage global contact channels, locations, and legal policies</p>
        </div>
      </div>

      {/* Main Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-8 space-y-6">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            <Icon icon="solar:phone-bold" className="w-4 h-4 text-cyan-500" />
            Contact & Channels
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Phone Number</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Icon icon="solar:phone-outline" className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  name="phone"
                  required
                  onChange={handleChange}
                  value={formData.phone}
                  placeholder="+8801700000000"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold text-slate-800"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Support Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Icon icon="solar:letter-outline" className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  name="email"
                  required
                  onChange={handleChange}
                  value={formData.email}
                  placeholder="support@caresee.com"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold text-slate-800"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Facebook Link</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Icon icon="solar:hashtag-outline" className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  name="facebook"
                  required
                  onChange={handleChange}
                  value={formData.facebook}
                  placeholder="https://facebook.com/..."
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold text-slate-800"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Instagram Link</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Icon icon="solar:instagram-outline" className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  name="instagram"
                  required
                  onChange={handleChange}
                  value={formData.instagram}
                  placeholder="https://instagram.com/..."
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold text-slate-800"
                />
              </div>
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">LinkedIn Link</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Icon icon="solar:share-circle-outline" className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  name="linkedin"
                  required
                  onChange={handleChange}
                  value={formData.linkedin}
                  placeholder="https://linkedin.com/company/..."
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold text-slate-800"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-8 space-y-6">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            <Icon icon="solar:pin-bold" className="w-4 h-4 text-indigo-500" />
            Descriptions & Location
          </h2>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Footer Description</label>
              <textarea
                name="description"
                required
                rows="3.5"
                onChange={handleChange}
                value={formData.description}
                placeholder="Write general footer brand introduction..."
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-medium text-slate-800"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Office Location (comma separated list)</label>
              <textarea
                name="location"
                required
                rows="3"
                onChange={handleChange}
                value={formData.location}
                placeholder="House 12, Road 4, Sector 7, Uttara, Dhaka"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-medium text-slate-800"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-8 space-y-6">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            <Icon icon="solar:document-text-bold" className="w-4 h-4 text-cyan-500" />
            Legal Policies
          </h2>

          <div className="space-y-5">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Terms & Conditions</label>
              <div className="rounded-xl overflow-hidden border border-slate-200">
                <JoditEditor
                  ref={termsRef}
                  value={formData?.termsAndCondition}
                  onChange={(newContent) => {
                    setFormData((prev) => ({
                      ...prev,
                      termsAndCondition: newContent,
                    }));
                  }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Privacy Policy</label>
              <div className="rounded-xl overflow-hidden border border-slate-200">
                <JoditEditor
                  ref={policyRef}
                  value={formData?.privacyPolicy}
                  onChange={(newContent) => {
                    setFormData((prev) => ({
                      ...prev,
                      privacyPolicy: newContent,
                    }));
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            className="w-full py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-xs rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
          >
            Update Footer & Legal Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default SuperAdminFooter;
